import { find } from "@kluntje/js-utils/lib/dom-helpers";
import { throttle } from "@kluntje/js-utils/lib/function-helpers/decorators";
import { ObserverCallback, ObserverService } from "@kluntje/services";
import { waitForScrollEnd } from "Helper/domHelper/waitForScrollEnd";

export type ScrollDirection = "UP" | "DOWN";

export interface SmoothScrollOptions {
  preventHeaderShrink?: boolean;
  additionalOffset?: number;
}

class WindowScrollService {
  public preventHeaderShrink = false;
  public preventHeaderGrow = false;

  static SCROLL_WHEEL_BUFFER = 50;

  private lastScrollTop: number;
  private lastDownScrollTop: number;
  private lastUpScrollTop: number;
  private _scrollDirection: ScrollDirection = "DOWN";

  constructor() {
    window.addEventListener("scroll", this.onWindowScroll.bind(this), { passive: true });
    this.lastScrollTop = this.scrollTop;
    this.lastDownScrollTop = this.scrollTop;
    this.lastUpScrollTop = this.scrollTop;
  }

  public get scrollDirection(): ScrollDirection {
    return this._scrollDirection;
  }

  public get scrollTop(): number {
    return window.scrollY || document.documentElement.scrollTop;
  }

  public observeScroll(callback: ObserverCallback) {
    ObserverService.observe(this.getObserverKey("both"), callback);
  }

  public unobserveScroll(callback: ObserverCallback) {
    ObserverService.unobserve(this.getObserverKey("both"), callback);
  }

  public observeScrollUp(callback: ObserverCallback) {
    ObserverService.observe(this.getObserverKey("up"), callback);
  }

  public unobserveScrollUp(callback: ObserverCallback) {
    ObserverService.unobserve(this.getObserverKey("up"), callback);
  }

  public observeScrollDown(callback: ObserverCallback) {
    ObserverService.observe(this.getObserverKey("down"), callback);
  }

  public unobserveScrollDown(callback: ObserverCallback) {
    ObserverService.unobserve(this.getObserverKey("down"), callback);
  }

  @throttle(100)
  private onWindowScroll() {
    this._scrollDirection = this.scrollTop > this.lastScrollTop ? "DOWN" : "UP";
    this.lastScrollTop = this.scrollTop;

    if (this._scrollDirection === "DOWN") {
      this.handleDownScroll();
    } else {
      this.handleUpScroll();
    }
  }

  private handleDownScroll() {
    const curScrollTop = this.scrollTop;
    this.lastDownScrollTop = curScrollTop;
    if (curScrollTop - WindowScrollService.SCROLL_WHEEL_BUFFER >= this.lastUpScrollTop) this.triggerCallbacks("DOWN");
  }

  private handleUpScroll() {
    const curScrollTop = this.scrollTop;
    this.lastUpScrollTop = curScrollTop;
    if (curScrollTop + WindowScrollService.SCROLL_WHEEL_BUFFER <= this.lastDownScrollTop) this.triggerCallbacks("UP");
  }

  private triggerCallbacks(scrollDirection: ScrollDirection) {
    if (scrollDirection === "UP") ObserverService.notifyObservers(this.getObserverKey("up"));
    if (scrollDirection === "DOWN") ObserverService.notifyObservers(this.getObserverKey("down"));
    ObserverService.notifyObservers(this.getObserverKey("both"));
  }

  public async scrollSmooth(scrollToOptions: ScrollToOptions, options?: SmoothScrollOptions) {
    const { preventHeaderShrink = false } = options || {};
    this.preventHeaderShrink = preventHeaderShrink;
    window.scrollTo({ ...scrollToOptions, behavior: "smooth" });
    await waitForScrollEnd(window);
    this.preventHeaderShrink = false;
  }

  public async scrollToElement(element: HTMLElement, options?: SmoothScrollOptions) {
    const { additionalOffset = 0 } = options || {};
    const offsetHeight = this.getPageTopOffsetHeight();
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offsetHeight - additionalOffset;

    await this.scrollSmooth({ top: offsetPosition }, options);
  }

  private getObserverKey(categoryName: string) {
    return `sde-window-scroll-service__${categoryName}`;
  }

  private getPageTopOffsetHeight() {
    const header = find(document, ".vic-m-header");
    const contextNavigation = find(document, ".vic-m-context-navigation");
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const contextNavigationHeight = contextNavigation ? contextNavigation.getBoundingClientRect().height : 0;

    return headerHeight + contextNavigationHeight + 40;
  }
}

export default new WindowScrollService();
